import { getToken } from './tokenManager';

// Crypto ใช้ Gateway ตัวเดียวกับหุ้น แต่ route แยกกันคนละชุด (/v1/crypto/...)
// ห้ามส่ง symbol แบบ X:BTCUSD ไป route หุ้น และ "BTC" เปล่าๆ คือหุ้น ไม่ใช่ crypto
const GATEWAY_BASE_URL = 'https://gateway.wethaiinvest.com';

// Server กำหนด batch สูงสุด 100 symbol ต่อ request
const CRYPTO_MAX_BATCH = 100;
// Server รับช่วงย้อนหลังได้ไม่เกิน 20 * 366 วัน
const CRYPTO_MAX_SPAN_DAYS = 20 * 366;
// โหลด catalog ใหม่ทุก 6 ชม. (ฝั่ง server refresh รอบเดียวกัน)
const CATALOG_TTL_MS = 6 * 60 * 60 * 1000;
// โหลด catalog ไม่สำเร็จ รอ 1 นาทีก่อนลองใหม่ (เท่ากับ cooldown ฝั่ง server)
const CATALOG_RETRY_MS = 60 * 1000;
// ราคาเก่ากว่านี้ถือว่าล่าช้า (เท่ากับ CRYPTO_STALE_AFTER=1h ฝั่ง server) ใช้ตอนไม่มีข้อมูลใหม่เข้ามาเลย
const CRYPTO_STALE_AFTER_MS = 60 * 60 * 1000;

export interface CryptoInstrument {
  symbol: string;        // X:BTCUSD
  pair: string;          // BTC-USD
  baseAsset: string;     // BTC
  quoteCurrency: string; // USD
  name: string;          // Bitcoin
  active: boolean;
}

export interface CryptoHealth {
  enabled: boolean;
  connected: boolean;
  lastEvent?: number;
  catalogSize?: number;
  catalogUpdatedAt?: number;
  catalogStale?: boolean;
  timezone?: string;
  marketMode?: string;
}

export interface CryptoPrice {
  symbol: string;
  assetClass: 'CRYPTO';
  quoteCurrency: 'USD';
  price?: string; // ทศนิยมแบบ string ไม่มีเมื่อ available:false
  source?: string;
  providerTimestamp?: number;
  updatedAt?: number;
  available: boolean;
  stale: boolean;
  status: 'OBSERVED' | 'LAST_KNOWN' | 'UNAVAILABLE' | string;
  marketMode?: string;
}

export interface CryptoBar {
  t: number;
  o: string;
  h: string;
  l: string;
  c: string;
  v: string;
}

export interface CryptoHistory {
  symbol: string;
  timeframe: 'day' | 'week' | 'month';
  timezone: 'UTC';
  results: CryptoBar[];
  source: string;
  coverage: string;
  provisional: boolean;
  stale: boolean;
  updatedAt: number;
}

export type CryptoTimeframe = 'day' | 'week' | 'month';

export function isCryptoSymbol(symbol: string | null | undefined): boolean {
  return !!symbol && /^X:[A-Z0-9]+$/i.test(symbol);
}

// X:BTCUSD -> BTC (ใช้แสดงผลเท่านั้น)
export function cryptoDisplaySymbol(symbol: string): string {
  const cached = catalogCache?.items.find(i => i.symbol === symbol);
  if (cached) return cached.baseAsset;
  return symbol.replace(/^X:/i, '').replace(/USD$/i, '');
}

// X:ETHUSD -> ETH/USD (แสดงผลเท่านั้น) / หุ้นคืนค่าเดิม
export function cryptoPairLabel(symbol: string | null | undefined): string {
  if (!symbol) return '';
  return isCryptoSymbol(symbol) ? `${cryptoDisplaySymbol(symbol)}/USD` : symbol;
}

// แทน X:ETHUSD ที่อยู่ในข้อความ (เช่นผลวิเคราะห์ AI) เป็น ETH/USD (แสดงผลเท่านั้น)
export function formatCryptoSymbolsInText(text: string): string {
  return text.replace(/\bX:([A-Z0-9]+?)USD\b/gi, (_, base: string) => `${base.toUpperCase()}/USD`);
}

// ใส่ symbol ลง path ตรงๆ ห้าม encodeURIComponent เพราะ ':' จะกลายเป็น %3A
// แล้ว Gateway ตอบ INVALID_CRYPTO_REQUEST — symbol ผ่าน isCryptoSymbol แล้วจึงไม่มีอักขระอันตราย
function cryptoPath(symbol: string): string {
  if (!isCryptoSymbol(symbol)) throw new Error(`Invalid crypto symbol: ${symbol}`);
  return `${GATEWAY_BASE_URL}/v1/crypto/${symbol.toUpperCase()}`;
}

function authHeaders(): Record<string, string> {
  return { Authorization: `Bearer ${getToken()}` };
}

// ---------------- Catalog ----------------

let catalogCache: { items: CryptoInstrument[]; loadedAt: number } | null = null;
let catalogInflight: Promise<CryptoInstrument[]> | null = null;
let catalogFailedAt = 0;

// ok = ใช้ได้, disabled = server ปิด crypto (CRYPTO_ENABLED=false → route หาย 404),
// unavailable = catalog ยังโหลดไม่ขึ้น (503 CRYPTO_CATALOG_UNAVAILABLE / เน็ตหลุด)
export type CryptoCatalogStatus = 'unknown' | 'ok' | 'disabled' | 'unavailable';
let catalogStatus: CryptoCatalogStatus = 'unknown';

export function getCryptoCatalogStatus(): CryptoCatalogStatus {
  return catalogStatus;
}

export async function fetchCryptoInstruments(): Promise<CryptoInstrument[]> {
  if (catalogCache && Date.now() - catalogCache.loadedAt < CATALOG_TTL_MS) {
    return catalogCache.items;
  }
  if (catalogInflight) return catalogInflight;
  if (catalogFailedAt && Date.now() - catalogFailedAt < CATALOG_RETRY_MS) {
    return catalogCache?.items || [];
  }

  catalogInflight = (async () => {
    try {
      const res = await fetch(`${GATEWAY_BASE_URL}/v1/crypto/instruments`, { headers: authHeaders() });
      if (!res.ok) {
        if (!catalogCache) catalogStatus = res.status === 404 ? 'disabled' : 'unavailable';
        throw new Error(`Gateway error: ${res.status} ${res.statusText}`);
      }
      const data = await res.json();
      const items: CryptoInstrument[] = data?.results || [];
      catalogCache = { items, loadedAt: Date.now() };
      catalogStatus = 'ok';
      catalogFailedAt = 0;
      return items;
    } catch (err) {
      catalogFailedAt = Date.now();
      if (!catalogCache && catalogStatus === 'unknown') catalogStatus = 'unavailable';
      console.error('Error fetching crypto instruments via gateway:', err);
      // ถ้าโหลดใหม่ไม่ได้ ใช้ของเก่าไปก่อน
      return catalogCache?.items || [];
    } finally {
      catalogInflight = null;
    }
  })();
  return catalogInflight;
}

// ค้นหาจาก catalog ฝั่ง client (ไม่มี search endpoint) เรียงตาม: ตรงเป๊ะ > ขึ้นต้นด้วย > มีคำนั้น
export async function searchCryptoInstruments(query: string, limit = 10): Promise<CryptoInstrument[]> {
  const items = (await fetchCryptoInstruments()).filter(i => i.active);
  const q = query.trim().toUpperCase().replace(/^X:/, '');
  if (!q) return items.slice(0, limit);

  const scored = items
    .map(item => {
      const base = item.baseAsset.toUpperCase();
      const name = (item.name || '').toUpperCase();
      const sym = item.symbol.toUpperCase().replace(/^X:/, '');
      let score = 0;
      if (base === q) score = 100;
      else if (base.startsWith(q)) score = 80 - base.length;
      else if (name.startsWith(q)) score = 50;
      else if (sym.includes(q) || name.includes(q)) score = 20;
      return { item, score };
    })
    .filter(x => x.score > 0)
    .sort((a, b) => b.score - a.score);

  return scored.slice(0, limit).map(x => x.item);
}

export async function fetchCryptoName(symbol: string): Promise<string | null> {
  const items = await fetchCryptoInstruments();
  return items.find(i => i.symbol === symbol)?.name || null;
}

// ---------------- Prices ----------------

// เก็บสถานะล่าสุดของแต่ละเหรียญ (stale/เวลา provider) ไว้ให้ UI อ่านได้โดยไม่ต้องยิงซ้ำ
const cryptoDetailsCache: {
  [symbol: string]: { stale: boolean; status: string; priceDecimal: string | null; providerTimestampIso: string | null };
} = {};

function storePriceDetails(p: CryptoPrice) {
  cryptoDetailsCache[p.symbol] = {
    stale: p.stale,
    status: p.status,
    priceDecimal: p.price ?? null,
    providerTimestampIso: p.providerTimestamp ? new Date(p.providerTimestamp).toISOString() : null,
  };
}

// ราคาจาก WebSocket (ดู realtimeSocket.ts) — stale ไม่ส่งมาเมื่อเป็น false
export function applyRealtimeCryptoQuote(q: { symbol: string; price: number; priceDecimal?: string; stale?: boolean; timestamp?: number }) {
  cryptoDetailsCache[q.symbol] = {
    stale: q.stale === true,
    status: q.stale ? 'LAST_KNOWN' : 'OBSERVED',
    priceDecimal: q.priceDecimal ?? String(q.price),
    providerTimestampIso: q.timestamp ? new Date(q.timestamp).toISOString() : null,
  };
}

// นอกจาก stale ที่ server ส่งมา ต้องเช็คอายุราคาเองด้วย (spec: client must age quotes locally)
// เผื่อ WS เงียบและ REST ก็ล่ม ราคาเก่าจะไม่ค้างโชว์ว่าสดอยู่
export function getStoredCryptoDetails(symbol: string) {
  const entry = cryptoDetailsCache[symbol];
  if (!entry) return { stale: false, status: 'UNKNOWN', priceDecimal: null, providerTimestampIso: null };
  const providerMs = entry.providerTimestampIso ? Date.parse(entry.providerTimestampIso) : NaN;
  const agedOut = Number.isFinite(providerMs) && Date.now() - providerMs > CRYPTO_STALE_AFTER_MS;
  return { ...entry, stale: entry.stale || agedOut };
}

// แปลงเป็น number เพื่อแสดงผลเท่านั้น ถ้าจะใช้คำนวณบัญชีให้ใช้ priceDecimal
function toDisplayPrice(p: CryptoPrice): number | null {
  if (!p.available || p.price == null) return null;
  const n = Number(p.price);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export async function fetchCryptoPrice(symbol: string): Promise<number | null> {
  try {
    const res = await fetch(`${cryptoPath(symbol)}/price`, {
      headers: authHeaders(),
    });
    if (!res.ok) return null;
    const data: CryptoPrice = await res.json();
    storePriceDetails(data);
    return toDisplayPrice(data);
  } catch (err) {
    console.error('Error fetching crypto price via gateway:', err);
    return null;
  }
}

export async function fetchCryptoPrices(symbols: string[]): Promise<{ [symbol: string]: number }> {
  const prices: { [symbol: string]: number } = {};
  const unique = Array.from(new Set(symbols.filter(isCryptoSymbol)));
  if (unique.length === 0) return prices;

  const chunks: string[][] = [];
  for (let i = 0; i < unique.length; i += CRYPTO_MAX_BATCH) {
    chunks.push(unique.slice(i, i + CRYPTO_MAX_BATCH));
  }

  await Promise.all(
    chunks.map(async chunk => {
      try {
        const res = await fetch(`${GATEWAY_BASE_URL}/v1/crypto/prices`, {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ symbols: chunk }),
        });
        if (!res.ok) throw new Error(`Gateway error: ${res.status} ${res.statusText}`);
        const data = await res.json();
        (data?.results || []).forEach((p: CryptoPrice) => {
          storePriceDetails(p);
          const price = toDisplayPrice(p);
          if (price !== null) prices[p.symbol] = price;
        });
      } catch (err) {
        console.error('Error fetching crypto batch prices via gateway:', err);
      }
    })
  );

  return prices;
}

// ---------------- History ----------------

function utcDateString(d: Date): string {
  return d.toISOString().slice(0, 10);
}

// ผลล่าสุดที่โหลดสำเร็จ แยกตาม symbol + timeframe
// เอาไว้คืนแทนเวลา server ตอบ 503 ชั่วคราว กราฟที่โชว์อยู่จะได้ไม่หายไปทั้งแผ่น
const lastGoodHistory = new Map<string, any[]>();

// คืนค่ารูปแบบเดียวกับ OHLC หุ้น ({t,o,h,l,c,v} เป็น number) เพื่อให้กราฟเดิมใช้ต่อได้เลย
export async function fetchCryptoAggregates(
  symbol: string,
  timeframe: CryptoTimeframe,
  from: string,
  to: string
): Promise<any[]> {
  const cacheKey = `${symbol}|${timeframe}`;
  try {
    // to ห้ามเกินวันนี้ (UTC) และช่วงห้ามเกิน 20*366 วัน
    const todayUtc = utcDateString(new Date());
    const toDate = to > todayUtc ? todayUtc : to;
    const minFrom = new Date(`${toDate}T00:00:00Z`);
    minFrom.setUTCDate(minFrom.getUTCDate() - (CRYPTO_MAX_SPAN_DAYS - 1));
    const fromDate = from < utcDateString(minFrom) ? utcDateString(minFrom) : from;

    const params = new URLSearchParams({ timeframe, from: fromDate, to: toDate });
    const url = `${cryptoPath(symbol)}/aggregates?${params.toString()}`;
    const res = await fetch(url, { headers: authHeaders() });

    // 503 = ยังไม่มีข้อมูลช่วงนี้/ติด cooldown ฝั่ง server ไม่ต้อง retry ถี่ (หน้ากราฟดึงใหม่ทุก 5 นาทีอยู่แล้ว)
    if (!res.ok) throw new Error(`Gateway error: ${res.status} ${res.statusText}`);

    const data: CryptoHistory = await res.json();
    const bars = (data?.results || []).map(bar => ({
      t: bar.t,
      o: Number(bar.o),
      h: Number(bar.h),
      l: Number(bar.l),
      c: Number(bar.c),
      v: Number(bar.v),
    }));
    lastGoodHistory.set(cacheKey, bars);
    return bars;
  } catch (err) {
    console.error('Error fetching crypto aggregates via gateway:', err);
    return lastGoodHistory.get(cacheKey) || [];
  }
}

// ---------------- Health ----------------

export async function fetchCryptoHealth(): Promise<CryptoHealth | null> {
  try {
    const res = await fetch(`${GATEWAY_BASE_URL}/v1/crypto/health`, { headers: authHeaders() });
    if (!res.ok) return null;
    return await res.json();
  } catch (err) {
    console.error('Error fetching crypto health via gateway:', err);
    return null;
  }
}
